<template>
  <div class="amprod-configurator-wrapper">
    <div v-if="isLoading" class="amprod-loading">
      <div class="amprod-loading-spinner" />
      <p>Loading...</p>
    </div>

    <div v-if="errorMessage" class="amprod-error">
      {{ errorMessage }}
    </div>

    <div v-if="!isLoading && !errorMessage" class="amprod-configurator-content">
      <h2>{{ productPartNumber }} Configuration</h2>

      <div class="amprod-configuration-container">
        <!-- Main content area with all slots stacked -->
        <div class="amprod-slots-container">
          <div
            v-for="slot in configData.configurable_slots"
            :key="slot.type ? slot.type.code : 'default'"
            class="amprod-slot-section"
          >
            <slot-options
              :slot-data="slot"
              :configuration="configuration"
              :compatibility-rules="configData.compatibility_rules || []"
              :configurable-slots="configData.configurable_slots"
              @option-selected="handleOptionSelect"
              @quantity-changed="handleQuantityChange"
            />
          </div>
        </div>

        <!-- Sticky sidebar with selected parts and quote button -->
        <configurator-sidebar
          :configuration="configuration"
          :configurable-slots="configData.configurable_slots"
          :product="configData.product"
          :is-configuration-valid="isConfigurationValid()"
          :is-edit-mode="isEditMode"
          :initial-quantity="initialConfigQuantity"
          @add-to-quote="handleAddToQuote"
          @option-quantity-change="handleOptionQuantityChange"
        />
      </div>
    </div>

    <!-- Toast Notifications -->
    <toast-notification ref="toast" />
  </div>
</template>

<script>
import SlotOptions from "./SlotOptions.vue";
import ConfiguratorSidebar from "./ConfiguratorSidebar.vue";
import ToastNotification from "./common/ToastNotification.vue";
import {
  getTriggeredRules,
  findOptionsMatchingConstraint,
  canDeselectOption,
  isOptionSelectedInConfiguration,
} from "../utils/compatibilityRules.js";

export default {
  name: "Configurator",
  components: {
    SlotOptions,
    ConfiguratorSidebar,
    ToastNotification,
  },
  props: {
    productPartNumber: {
      type: String,
      required: true,
    },
    baseUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      configData: {
        product: {},
        configurable_slots: [],
        compatibility_rules: [],
      },
      configuration: {},
      isEditMode: false,
      editConfigId: null,
      originalCartItem: null,
      initialConfigQuantity: 1,
    };
  },
  watch: {
    productPartNumber: {
      immediate: true,
      handler(newPartNumber) {
        if (newPartNumber) {
          this.loadConfiguration(newPartNumber);
        } else {
          console.warn(" Product part number is empty or undefined");
        }
      },
    },
  },
  created() {
    // Check for edit mode early but don't preload yet
    const urlParams = new URLSearchParams(window.location.search);
    const editConfigId = urlParams.get("edit_config");

    if (editConfigId && window.amprodCart) {
      const cartItem = window.amprodCart.getCartItemById(editConfigId);
      if (cartItem) {
        // make sure the cartItem's part number matches the current product
        if (cartItem.product.part_number !== this.productPartNumber) {
          console.warn(
            "Edit config part number does not match current product:",
            cartItem.part_number,
            this.productPartNumber
          );
          return;
        }

        // Init edit mode state
        this.isEditMode = true;
        this.editConfigId = editConfigId;
        this.originalCartItem = cartItem;
        console.log(
          "Edit mode detected for config ID:",
          editConfigId,
          cartItem
        );
      }
    }
  },
  mounted() {
    console.log("Component mounted to DOM");
  },
  methods: {
    async loadConfiguration(partNumber) {
      console.log("Loading configuration for part number:", partNumber);
      console.log("Using base URL:", this.baseUrl);

      this.isLoading = true;
      this.errorMessage = null;

      try {
        const apiUrl = `${this.baseUrl}/products/configurator/${partNumber}`;
        console.log("Fetching from:", apiUrl);

        const response = await fetch(apiUrl);
        console.log("API response status:", response.status);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch configuration data: ${response.status} ${response.statusText}`
          );
        }

        this.configData = await response.json();
        console.log("Configuration data loaded:", this.configData);

        // Reset configuration only if not in edit mode
        if (!this.isEditMode) {
          this.configuration = {};
        } else if (this.originalCartItem) {
          // Load the configuration from the cart item after data is loaded
          console.log(
            "Loading configuration from cart item:",
            this.originalCartItem
          );
          this.loadConfigurationFromCartItem(this.originalCartItem);
        }

        // No need to set currentSlot anymore since we show all slots
      } catch (error) {
        console.error("Error loading configuration:", error);
        this.errorMessage =
          "Failed to load product configuration. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    handleOptionSelect(slotId, option, action = "select") {
      if (action === "deselect") {
        // Check if option can be deselected (not required by another selected option)
        const deselectionCheck = canDeselectOption(
          option,
          this.configuration,
          this.configData.compatibility_rules || []
        );

        if (!deselectionCheck.canDeselect) {
          this.$refs.toast.show(
            deselectionCheck.reason ||
              "This option is required by another selected option.",
            "warning",
            5000,
            "Cannot Remove Option"
          );
          return;
        }

        // Proceed with deselection
        if (!this.configuration[slotId]) {
          this.configuration[slotId] = [];
        }

        if (!Array.isArray(this.configuration[slotId])) {
          this.configuration[slotId] = [this.configuration[slotId]];
        }

        const updatedSlot = this.configuration[slotId].filter(
          (selected) => selected.id !== option.id
        );

        if (updatedSlot.length === 0) {
          // Create new config object without this slot for reactivity
          const newConfig = { ...this.configuration };
          delete newConfig[slotId];
          this.configuration = newConfig;
        } else {
          this.configuration[slotId] = updatedSlot;
        }

        // Check if any selected options depended on this option (required_first rules)
        this.removeRequiredFirstDependents(option);

        return;
      }

      // Handle selection
      if (!this.configuration[slotId]) {
        this.configuration[slotId] = [];
      }

      if (!Array.isArray(this.configuration[slotId])) {
        this.configuration[slotId] = [this.configuration[slotId]];
      }

      const slotData = this.configData.configurable_slots.find((slot) => {
        const slotDataId = slot.type?.code || slot.id || "default";
        return slotDataId === slotId;
      });

      const maxSelections = slotData?.max_selections;
      const optionWithQuantity = {
        ...option,
        quantity: 1,
      };

      if (maxSelections === 1) {
        this.configuration[slotId] = [optionWithQuantity];
      } else {
        const isAlreadySelected = this.configuration[slotId].some(
          (selected) => selected.id === option.id
        );

        if (!isAlreadySelected) {
          const currentUsage = this.configuration[slotId].reduce(
            (total, selected) => total + (selected.quantity || 1),
            0
          );

          if (maxSelections === null || currentUsage + 1 <= maxSelections) {
            this.configuration[slotId].push(optionWithQuantity);
          }
        }
      }

      // Check for triggered rules (suggested and required)
      this.processTriggeredRules(option);
    },

    processTriggeredRules(selectedOption) {
      const triggeredRules = getTriggeredRules(
        selectedOption,
        this.configData.compatibility_rules || []
      );

      for (const rule of triggeredRules) {
        const isFirstOption =
          selectedOption.id === rule.first_option?.id ||
          (selectedOption.option_groups &&
            selectedOption.option_groups.includes(rule.first_option_group?.id));

        const isSecondOption =
          selectedOption.id === rule.second_option?.id ||
          (selectedOption.option_groups &&
            selectedOption.option_groups.includes(
              rule.second_option_group?.id
            ));

        // Determine which constraint to auto-select
        let targetOptionId = null;
        let targetOptionGroupId = null;

        if (isFirstOption) {
          targetOptionId = rule.second_option?.id || null;
          targetOptionGroupId = rule.second_option_group?.id || null;
        } else if (isSecondOption) {
          targetOptionId = rule.first_option?.id || null;
          targetOptionGroupId = rule.first_option_group?.id || null;
        }

        // Check if target is already selected
        const alreadySelected = isOptionSelectedInConfiguration(
          this.configuration,
          targetOptionId,
          targetOptionGroupId
        );

        if (alreadySelected) {
          continue; // Skip if already selected
        }

        // Find the target option(s) in available slots
        const targetMatches = findOptionsMatchingConstraint(
          this.configData.configurable_slots,
          targetOptionId,
          targetOptionGroupId
        );

        if (targetMatches.length === 0) {
          continue; // No matching options found
        }

        // For "suggested" rules, auto-select the first match and show notification
        if (rule.rule_type === "suggested") {
          const { slot, option } = targetMatches[0];
          const targetSlotId = slot.type?.code || slot.id || "default";

          // Auto-select the option
          if (!this.configuration[targetSlotId]) {
            this.configuration[targetSlotId] = [];
          }

          if (!Array.isArray(this.configuration[targetSlotId])) {
            this.configuration[targetSlotId] = [
              this.configuration[targetSlotId],
            ];
          }

          const optionWithQuantity = {
            ...option,
            quantity: 1,
          };

          const isAlreadyInSlot = this.configuration[targetSlotId].some(
            (selected) => selected.id === option.id
          );

          if (!isAlreadyInSlot) {
            this.configuration[targetSlotId].push(optionWithQuantity);

            // Show toast notification with clear title
            const title = `"${option.description}" Also Added`;
            const message =
              rule.description ||
              "This option has been automatically added to your configuration.";
            this.$refs.toast.show(message, "info", 6000, title);
          }
        }

        // For "required" rules, auto-select the first match
        if (rule.rule_type === "required") {
          const { slot, option } = targetMatches[0];
          const targetSlotId = slot.type?.code || slot.id || "default";

          // Auto-select the option
          if (!this.configuration[targetSlotId]) {
            this.configuration[targetSlotId] = [];
          }

          if (!Array.isArray(this.configuration[targetSlotId])) {
            this.configuration[targetSlotId] = [
              this.configuration[targetSlotId],
            ];
          }

          const optionWithQuantity = {
            ...option,
            quantity: 1,
          };

          const isAlreadyInSlot = this.configuration[targetSlotId].some(
            (selected) => selected.id === option.id
          );

          if (!isAlreadyInSlot) {
            this.configuration[targetSlotId].push(optionWithQuantity);

            // Show toast notification with clear title
            const title = `"${option.description}" Also Added`;
            const message =
              rule.description ||
              "This option is required and has been added to your configuration.";
            this.$refs.toast.show(message, "info", 6000, title);
          }
        }
      }
    },

    removeRequiredFirstDependents(deselectedOption) {
      // Find all required_first rules where the deselected option is the "second" (prerequisite)
      const rules = this.configData.compatibility_rules || [];
      const removedOptions = [];

      for (const rule of rules) {
        if (rule.rule_type !== "required_first") continue;

        // Check if the deselected option is the prerequisite (second option)
        const isPrerequisite =
          deselectedOption.id === rule.second_option?.id ||
          (deselectedOption.option_groups &&
            deselectedOption.option_groups.includes(
              rule.second_option_group?.id
            ));

        if (!isPrerequisite) continue;

        // Find and remove any selected options that match the "first" constraint
        for (const slotId in this.configuration) {
          const slotConfig = this.configuration[slotId];
          const selectedOptions = Array.isArray(slotConfig)
            ? slotConfig
            : [slotConfig];

          for (const selectedOption of selectedOptions) {
            const isDependentOption =
              selectedOption.id === rule.first_option?.id ||
              (selectedOption.option_groups &&
                selectedOption.option_groups.includes(
                  rule.first_option_group?.id
                ));

            if (isDependentOption) {
              // Remove this option from configuration using Vue reactivity
              const updatedSlot = this.configuration[slotId].filter(
                (opt) => opt.id !== selectedOption.id
              );

              if (updatedSlot.length === 0) {
                // Use Vue.delete or create new object without this key for reactivity
                const newConfig = { ...this.configuration };
                delete newConfig[slotId];
                this.configuration = newConfig;
              } else {
                this.configuration[slotId] = updatedSlot;
              }

              // Track for notification
              removedOptions.push({
                option: selectedOption,
                rule: rule,
              });
            }
          }
        }
      }

      // Show toast notifications for removed options
      if (removedOptions.length > 0) {
        for (const { option, rule } of removedOptions) {
          const title = `"${option.description}" Removed`;
          const message =
            rule.description ||
            `This option was removed because it requires ${deselectedOption.description}.`;
          this.$refs.toast.show(message, "warning", 6000, title);
        }
      }
    },

    handleQuantityChange(slotId, option, newQuantity) {
      if (!this.configuration[slotId]) {
        return;
      }

      // Find the slot configuration to check max_selections
      const slotData = this.configData.configurable_slots.find((slot) => {
        const slotDataId = slot.type?.code || slot.id || "default";
        return slotDataId === slotId;
      });

      const maxSelections = slotData?.max_selections;

      // If there's a max selection limit, validate the quantity change
      if (maxSelections !== null) {
        // Calculate total selections if we apply this quantity change
        const otherSelectionsCount = this.configuration[slotId].filter(
          (selected) => selected.id !== option.id
        ).length;

        // Check if the new quantity would exceed the slot limit
        if (otherSelectionsCount + newQuantity > maxSelections) {
          // Don't allow the change - could show a warning here
          console.warn(
            `Quantity ${newQuantity} would exceed slot limit of ${maxSelections}`
          );
          return;
        }
      }

      // Find and update the option's quantity
      const optionIndex = this.configuration[slotId].findIndex(
        (selected) => selected.id === option.id
      );

      if (optionIndex !== -1) {
        // Update the quantity for this specific option
        this.configuration[slotId][optionIndex] = {
          ...this.configuration[slotId][optionIndex],
          quantity: newQuantity,
        };
      }
    },

    handleOptionQuantityChange(slotId, option, newQuantity) {
      console.log(
        "handleOptionQuantityChange called:",
        slotId,
        option,
        newQuantity
      );
      // If quantity reaches 0, deselect the option
      if (newQuantity === 0) {
        console.log("Deselecting option");
        this.handleOptionSelect(slotId, option, "deselect");
        return;
      }

      // Otherwise, update the quantity
      this.handleQuantityChange(slotId, option, newQuantity);
    },

    getSlotName(slotId) {
      const slot = this.configData.configurable_slots.find((slot) => {
        const slotDataId = slot.type?.code || slot.id || "default";
        return slotDataId === slotId;
      });

      return slot?.name || slot?.type?.name || slotId;
    },

    isConfigurationValid() {
      // Check if all slots meet their minimum selection requirements
      for (const slot of this.configData.configurable_slots) {
        const slotId = slot.type?.code || slot.id || "default";
        const minSelections = slot.min_selections || 0;

        if (minSelections > 0) {
          const selections = this.configuration[slotId];
          const selectionCount = Array.isArray(selections)
            ? selections.length
            : selections
            ? 1
            : 0;

          if (selectionCount < minSelections) {
            return false;
          }
        }
      }

      return true;
    },

    handleAddToQuote(quoteData) {
      // Check if we're in edit mode
      if (this.isEditMode && this.editConfigId) {
        // Update existing cart item
        if (window.amprodCart && window.amprodCart.updateCartItem) {
          const success = window.amprodCart.updateCartItem(
            this.editConfigId,
            quoteData
          );

          if (success) {
            // For edit mode, redirect back after a short delay
            setTimeout(() => {
              this.isEditMode = false;
              window.history.back();
            }, 2000);

            // Reset configuration and edit mode
            this.configuration = {};
            this.editConfigId = null;
            this.originalCartItem = null;
          } else {
            alert("Failed to update configuration. Please try again.");
          }
        } else {
          console.warn("Cart update functionality is not available.");
          alert("Cart update functionality is not available.");
        }
        return;
      }

      // Normal add to cart flow
      if (window.amprodCart) {
        window.amprodCart.addToCart(quoteData);
      } else {
        console.warn("Cart functionality is not available at the moment.");
        alert("Cart functionality is not available at the moment.");
        return;
      }

      // Reset configuration
      this.configuration = {};
    },

    loadConfigurationFromCartItem(cartItem) {
      console.log("loadConfigurationFromCartItem called with:", cartItem);
      console.log("Available slots:", this.configData.configurable_slots);

      // Set the initial quantity from the cart item
      this.initialConfigQuantity = cartItem.quantity || 1;
      console.log(
        "Setting initial config quantity to:",
        this.initialConfigQuantity
      );

      // Reset configuration
      this.configuration = {};

      // Group options by slot based on the configured slots
      if (cartItem.options && cartItem.options.length > 0) {
        console.log("Processing cart item options:", cartItem.options);

        // For each option in the cart item, find which slot it belongs to
        cartItem.options.forEach((option) => {
          console.log("Processing option:", option);

          // Find the slot this option belongs to by checking all available options in each slot
          for (const slot of this.configData.configurable_slots) {
            const slotId = slot.type?.code || slot.id || "default";
            console.log(
              "Checking slot:",
              slotId,
              "with options:",
              slot.options
            );

            const optionExists = slot.options?.some((slotOption) => {
              const match =
                slotOption.id === option.id ||
                slotOption.part_number === option.part_number;
              if (match) {
                console.log(
                  "Found matching option:",
                  slotOption,
                  "for cart option:",
                  option
                );
              }
              return match;
            });

            if (optionExists) {
              if (!this.configuration[slotId]) {
                this.configuration[slotId] = [];
              }

              // Ensure we're working with an array
              if (!Array.isArray(this.configuration[slotId])) {
                this.configuration[slotId] = [this.configuration[slotId]];
              }

              // Find the full option data from the slot
              const fullOption = slot.options.find(
                (slotOption) =>
                  slotOption.id === option.id ||
                  slotOption.part_number === option.part_number
              );

              // Add the option with its quantity, using full option data
              const optionToAdd = {
                ...(fullOption || option),
                quantity: option.quantity || 1,
              };

              console.log("Adding option to slot", slotId, ":", optionToAdd);
              this.configuration[slotId].push(optionToAdd);
              break; // Found the slot, no need to continue searching
            }
          }
        });

        console.log("Final loaded configuration:", this.configuration);
      } else {
        console.log("No options found in cart item");
      }
    },
  },
};
</script>

<style scoped>
.amprod-configurator-content {
  margin: 20px 0;
}

.amprod-configuration-container {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.amprod-slots-container {
  flex: 1;
  min-width: 0; /* Allow flex shrinking */
}

.amprod-slot-section {
  margin-bottom: 40px;
  position: relative;
}

.amprod-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.amprod-loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0073aa;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.amprod-error {
  color: #d32f2f;
  background-color: #fde8e8;
  padding: 15px;
  border-radius: 4px;
  margin: 20px 0;
}

/* Responsive design */
@media (max-width: 1024px) {
  .amprod-configuration-container {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .amprod-slot-section {
    margin-bottom: 30px;
  }
}
</style>
